import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface BillingStatus {
  status: 'trialing' | 'active' | 'past_due' | 'blocked' | 'canceled';
  currentPeriodEnd: string | null;
  blockedAt: string | null;
  blocked: boolean;
  manualOverrideEnabled: boolean;
}

interface BillingContextType {
  billing: BillingStatus | null;
  blocked: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const BillingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    if (!isAuthenticated || !isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get('/billing/status');
      setBilling(response.data);
      setBlocked(!!response.data.blocked);
    } catch (error) {
      // Falha ao consultar billing não deve travar o app — assume não-bloqueado
      // (o middleware do backend é a fonte de verdade real do bloqueio).
      console.error('Erro ao buscar status de billing:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin]);

  useEffect(() => {
    const handleBlocked = () => setBlocked(true);
    window.addEventListener('billing:blocked', handleBlocked);
    return () => window.removeEventListener('billing:blocked', handleBlocked);
  }, []);

  return (
    <BillingContext.Provider value={{ billing, blocked, loading, refresh: fetchStatus }}>
      {children}
    </BillingContext.Provider>
  );
};

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling deve ser usado dentro de um BillingProvider');
  }
  return context;
};
